/**
 * One project write at a time, per tab.
 *
 * `projects.updated_at` is the save token: a save presents the version it
 * believes the server holds, and the server refuses a spent one with
 * PROJECT_STALE_WRITE. That protects one device from another. It does not
 * protect a tab from ITSELF. After a project opens, background repairs (the
 * cloud photo re-key, a video File scrub) save to the server gated on the same
 * token the foreground is holding. When two such writes overlap, whichever
 * reaches the server second is refused.
 *
 * Production, 2026-09-20 (cloud, v6.0.21): every queued project failed Stage 1
 * with PROJECT_STALE_WRITE exactly one second after its last photo upload. The
 * background re-key spends about two minutes uploading a fresh import's photos
 * to S3, then saves; the Quote Run's evidence checkpoint, still holding the
 * version from when the project opened, was refused. On a local stack the same
 * uploads finish in milliseconds, before the run's first save, so the repair's
 * new version is adopted in time and the collision never shows.
 *
 * Serialising the "read the token, send, adopt the new token" section removes
 * the overlap. A task runs after the previous one SETTLES, whether it resolved
 * or rejected: one failed save must never wedge every later save.
 *
 * `isBusy()` exists for the remote-version monitor. While one of this tab's own
 * writes is in flight the server already holds a version the tab has not
 * adopted yet, and that must never be read as somebody else's edit.
 */
export type ProjectWriteLock = (<T>(task: () => Promise<T>) => Promise<T>) & { isBusy: () => boolean };

export function createProjectWriteLock(): ProjectWriteLock {
    let tail: Promise<void> = Promise.resolve();
    let outstanding = 0;
    const withProjectWriteLock = <T>(task: () => Promise<T>): Promise<T> => {
        outstanding += 1;
        const run = tail.then(task);
        tail = run.then(() => undefined, () => undefined).then(() => { outstanding -= 1; });
        return run;
    };
    return Object.assign(withProjectWriteLock, { isBusy: () => outstanding > 0 });
}

/** The tab's one lock. A tab is exactly one instance of this module. */
export const tabProjectWriteLock: ProjectWriteLock = createProjectWriteLock();
